import React, { useState } from 'react';
import './Contact.css';
import siteContent from '../../data/siteContent';
import { db } from '../../firebase';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';

const Contact = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    businessName: '',
    email: '',
    services: '',
    project: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({
    type: '',
    message: ''
  });

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const validateField = (name, value) => {
    const trimmedValue = value.trim();

    switch (name) {
      case 'firstName':
        if (!trimmedValue) return 'First name is required.';
        return '';

      case 'lastName':
        if (!trimmedValue) return 'Last name is required.';
        return '';

      case 'businessName':
        if (!trimmedValue) return 'Business name is required.';
        return '';

      case 'email':
        if (!trimmedValue) return 'Email is required.';
        if (!emailRegex.test(trimmedValue)) return 'Please enter a valid email address.';
        return '';

      case 'services':
        if (!trimmedValue) return 'Please tell us what services you are interested in.';
        return '';

      case 'project':
        return '';

      default:
        return '';
    }
  };

  const validateForm = () => {
    const newErrors = {
      firstName: validateField('firstName', formData.firstName),
      lastName: validateField('lastName', formData.lastName),
      businessName: validateField('businessName', formData.businessName),
      email: validateField('email', formData.email),
      services: validateField('services', formData.services),
      project: validateField('project', formData.project)
    };

    setErrors(newErrors);

    return !Object.values(newErrors).some((error) => error);
  };

  const handleChange = (event) => {
    const { name, value } = event.target;

    setFormData((prevData) => ({
      ...prevData,
      [name]: value
    }));

    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: validateField(name, value)
    }));

    if (submitStatus.message) {
      setSubmitStatus({ type: '', message: '' });
    }
  };

  const handleBlur = (event) => {
    const { name, value } = event.target;

    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: validateField(name, value)
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateForm()) {
      setSubmitStatus({
        type: 'error',
        message: 'Please fix the errors and try again.'
});
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus({ type: '', message: '' });

    try {
      await addDoc(collection(db, 'consultationRequests'), {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        businessName: formData.businessName.trim(),
        email: formData.email.trim(),
        services: formData.services.trim(),
        project: formData.project.trim(),
        createdAt: serverTimestamp()
      });

      setFormData({
        firstName: '',
        lastName: '',
        businessName: '',
        email: '',
        services: '',
        project: ''
      });

      setErrors({});

      setSubmitStatus({
        type: 'success',
        message: 'Thanks. Your request was submitted successfully.'
      });
    } catch (error) {
      setSubmitStatus({
        type: 'error',
        message: 'Something went wrong while submitting the form. Please try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="contact-section" id="contact">
      <div className="contact-section__inner">
        <div className="contact-section__content">
          <h2 className="contact-section__title">{siteContent.contact.title}</h2>
          <p className="contact-section__text">{siteContent.contact.description}</p>
        </div>

        <form className="contact-form" onSubmit={handleSubmit} noValidate>
          <div className="contact-form__row">
            <div className="contact-form__field">
              <label htmlFor="firstName">
  First Name <span className="contact-form__required">*</span>
</label>
              <input
                id="firstName"
                name="firstName"
                type="text"
                value={formData.firstName}
                onChange={handleChange}
                onBlur={handleBlur}
                autoComplete="given-name"
                required
                aria-invalid={Boolean(errors.firstName)}
              />
              {errors.firstName ? <p className="contact-form__error">{errors.firstName}</p> : null}
            </div>

            <div className="contact-form__field">
              <label htmlFor="lastName">
  Last Name <span className="contact-form__required">*</span>
</label>
              <input
                id="lastName"
                name="lastName"
                type="text"
                value={formData.lastName}
                onChange={handleChange}
                onBlur={handleBlur}
                autoComplete="family-name"
                required
                aria-invalid={Boolean(errors.lastName)}
              />
              {errors.lastName ? <p className="contact-form__error">{errors.lastName}</p> : null}
            </div>
          </div>

          <div className="contact-form__row">
            <div className="contact-form__field">
              <label htmlFor="businessName">
  Business Name <span className="contact-form__required">*</span>
</label>
              <input
                id="businessName"
                name="businessName"
                type="text"
                value={formData.businessName}
                onChange={handleChange}
                onBlur={handleBlur}
                autoComplete="organization"
                required
                aria-invalid={Boolean(errors.businessName)}
              />
              {errors.businessName ? <p className="contact-form__error">{errors.businessName}</p> : null}
            </div>

            <div className="contact-form__field">
              <label htmlFor="email">
  Email <span className="contact-form__required">*</span>
</label>
              <input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                onBlur={handleBlur}
                autoComplete="email"
                required
                aria-invalid={Boolean(errors.email)}
              />
              {errors.email ? <p className="contact-form__error">{errors.email}</p> : null}
            </div>
          </div>

          <div className="contact-form__field">
  <label htmlFor="services">
    What services are you interested in? <span className="contact-form__required">*</span>
  </label>
  <select
    id="services"
    name="services"
    value={formData.services}
    onChange={handleChange}
    onBlur={handleBlur}
    required
    aria-invalid={Boolean(errors.services)}
  >
    <option value="">Select a service</option>
    <option value="Brand & Identity">Brand & Identity</option>
    <option value="Efficiency">Efficiency</option>
    <option value="Growth and Intelligence">Growth and Intelligence</option>
    <option value="Multiple Services">Multiple Services</option>
    <option value="Not Sure Yet">Not Sure Yet</option>
  </select>
  {errors.services ? <p className="contact-form__error">{errors.services}</p> : null}
</div>

          <div className="contact-form__field">
            <label htmlFor="project">Tell us about your project</label>
            <textarea
              id="project"
              name="project"
              rows="6"
              value={formData.project}
              onChange={handleChange}
              onBlur={handleBlur}
            />
          </div>

          {submitStatus.message ? (
            <p className={`contact-form__status contact-form__status--${submitStatus.type}`}>
              {submitStatus.message}
            </p>
          ) : null}

          <button className="contact-form__button" type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Submitting...' : 'Contact Us'}
          </button>
        </form>
      </div>
    </section>
  );
};

export default Contact;